import type { CMField } from '../add-edit.types';
import type { FieldItem } from '../../../../../Components/Controls/cm-control-config';
import type { FieldSetupAccess, FieldSetupToggleKey } from './field-setup-card.types';

/**
 * Reads the setup card's Alias Name and toggle rows for a field, in the order Figma lists them for
 * that field type. Source and Calculation keep their own controls, so they get none here.
 * @param field - The field being set up.
 * @param item - The field's config item.
 * @returns The alias and toggle state, or undefined when the card shows neither for this type.
 */
export function getFieldSetupAccess(field: CMField, item: FieldItem): FieldSetupAccess | undefined {
  const toggleKeys = SETUP_TOGGLES_BY_TYPE[item.type];
  if (!toggleKeys) return undefined;
  const config = item.config as { required?: boolean };
  const checkedByKey: Record<FieldSetupToggleKey, boolean> = {
    required: !!config.required,
    hasSupplierToggle: !!field.hasSupplierToggle,
    isVisibleToSupplier: field.isVisibleToSupplier !== false,
    isEditableBySupplier: field.isEditableBySupplier !== false,
    showCanvasTitleInput: field.showCanvasTitleInput !== false,
    showCanvasNotes: field.showCanvasNotes !== false,
  };
  return {
    ...(ALIAS_FIELD_TYPES.includes(item.type) ? { aliasName: field.aliasName ?? '' } : {}),
    toggles: toggleKeys.map((key) => ({ key, checked: checkedByKey[key] })),
  };
}

const SUPPLIER_TOGGLES: FieldSetupToggleKey[] = ['required', 'isVisibleToSupplier', 'isEditableBySupplier'];

const SETUP_TOGGLES_BY_TYPE: Partial<Record<FieldItem['type'], FieldSetupToggleKey[]>> = {
  dropdown: SUPPLIER_TOGGLES,
  text: SUPPLIER_TOGGLES,
  radio: SUPPLIER_TOGGLES,
  checkbox: SUPPLIER_TOGGLES,
  date: SUPPLIER_TOGGLES,
  image: SUPPLIER_TOGGLES,
  time: SUPPLIER_TOGGLES,
  comment: ['required', 'hasSupplierToggle', 'isVisibleToSupplier', 'isEditableBySupplier'],
  canvas: ['showCanvasTitleInput', 'showCanvasNotes'],
  status: ['isVisibleToSupplier', 'isEditableBySupplier'],
};

const ALIAS_FIELD_TYPES: ReadonlyArray<FieldItem['type']> = ['dropdown', 'text', 'radio', 'checkbox', 'date', 'image', 'comment', 'canvas'];
